import { Smartphone, RefreshCw } from "lucide-react";
import AdminMiniStat from "../../components/admin/AdminMiniStat";
import AdminEsimOrderLogsPanel from "../../components/admin/AdminEsimOrderLogsPanel";

export default function AdminEsimTab({
  esimOverview,
  esimActivations,
  esimOrderLogs,
  esimLoading,
  esimLogsFilter,
  esimCatalogSyncing,
  onLogsFilterChange,
  onRefresh,
  onSyncCatalog,
}) {
  return (
    <>
      <div className="zc-card p-5 mt-4" data-testid="admin-esim-overview">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: "rgba(16,185,129,0.12)" }}>
            <Smartphone size={18} style={{ color: "var(--zc-success)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="zc-title text-lg">Travel Pack · Airalo</div>
            {esimLoading && !esimOverview && (
              <div className="text-xs text-[var(--zc-text-3)] mt-2">Loading…</div>
            )}
            {esimOverview && (
              <>
                <div className="text-xs text-[var(--zc-text-2)] mt-2 space-y-1">
                  <div>
                    <b>Configured:</b>{" "}
                    <span style={{ color: esimOverview.airalo?.configured ? "var(--zc-success)" : "var(--zc-danger,#dc2626)" }}>
                      {esimOverview.airalo?.configured ? "Yes — live orders enabled" : "No — checkout will reject add_esim"}
                    </span>
                  </div>
                  <div><b>Environment:</b> {esimOverview.airalo?.environment || "—"} · <b>API base:</b> <span className="font-mono break-all">{esimOverview.airalo?.api_base_url || "—"}</span></div>
                  {esimOverview.airalo?.client_id_hint && <div><b>Client ID hint:</b> {esimOverview.airalo.client_id_hint}</div>}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                  <AdminMiniStat label="Live (Airalo)" v={esimOverview.counts?.activations_airalo} ok />
                  <AdminMiniStat label="Mock / dev" v={esimOverview.counts?.activations_mock} warn={esimOverview.counts?.activations_mock > 0} />
                  <AdminMiniStat label="Orders OK" v={esimOverview.counts?.order_logs_success} ok />
                  <AdminMiniStat label="Orders failed" v={esimOverview.counts?.order_logs_failed} warn={esimOverview.counts?.order_logs_failed > 0} />
                  <AdminMiniStat label="Total activations" v={esimOverview.counts?.activations_total} />
                </div>
                {esimOverview.airalo?.configured && esimOverview.catalog_sync && (
                  <div className="mt-4 p-3 rounded border border-[var(--zc-border)] text-xs space-y-2" data-testid="admin-esim-catalog-sync">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="eyebrow">Package catalog (GET /v2/packages)</div>
                      <button
                        type="button"
                        onClick={onSyncCatalog}
                        disabled={esimCatalogSyncing || esimOverview.catalog_sync.sync_in_progress}
                        className="px-3 h-8 zc-btn-ghost text-xs flex items-center gap-1"
                        data-testid="admin-esim-catalog-sync-btn"
                      >
                        <RefreshCw size={12} className={esimCatalogSyncing ? "animate-spin" : ""} />
                        {esimCatalogSyncing ? "Syncing…" : "Sync now"}
                      </button>
                    </div>
                    <div className="text-[var(--zc-text-2)] space-y-0.5">
                      <div>
                        <b>Last synced:</b>{" "}
                        {esimOverview.catalog_sync.last_synced_at
                          ? new Date(esimOverview.catalog_sync.last_synced_at).toLocaleString()
                          : "Never"}
                        {typeof esimOverview.catalog_sync.last_sync_age_sec === "number" && (
                          <> · {Math.round(esimOverview.catalog_sync.last_sync_age_sec / 60)} min ago</>
                        )}
                      </div>
                      <div>
                        <b>Packages:</b> {esimOverview.catalog_sync.package_count ?? 0}
                        {" · "}
                        <b>Interval:</b> {Math.round((esimOverview.catalog_sync.sync_interval_sec || 3600) / 60)} min
                      </div>
                      <div>
                        <b>Status:</b>{" "}
                        <span style={{
                          color: esimOverview.catalog_sync.is_stale
                            ? "var(--zc-warn,#f59e0b)"
                            : esimOverview.catalog_sync.last_status === "ok"
                              ? "var(--zc-success)"
                              : "var(--zc-danger,#dc2626)",
                        }}>
                          {esimOverview.catalog_sync.sync_in_progress
                            ? "Sync in progress…"
                            : esimOverview.catalog_sync.is_stale
                              ? "Stale — sync overdue"
                              : esimOverview.catalog_sync.last_status === "ok"
                                ? "Fresh"
                                : esimOverview.catalog_sync.last_status || "Unknown"}
                        </span>
                      </div>
                      {esimOverview.catalog_sync.last_error && (
                        <div style={{ color: "var(--zc-danger,#dc2626)" }}>
                          <b>Last error:</b> {typeof esimOverview.catalog_sync.last_error === "string"
                            ? esimOverview.catalog_sync.last_error
                            : JSON.stringify(esimOverview.catalog_sync.last_error)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!esimOverview.airalo?.configured && (
                  <div className="mt-4 text-xs p-3 rounded border border-[var(--zc-border)]" style={{ background: "rgba(245,158,11,0.08)", color: "var(--zc-warn,#f59e0b)" }}>
                    Set <span className="font-mono">AIRALO_CLIENT_ID</span> and <span className="font-mono">AIRALO_CLIENT_SECRET</span> in backend/.env.
                    Use <span className="font-mono">AIRALO_API_BASE_URL</span> to point at sandbox/dev if needed.
                    Mock activations (<span className="font-mono">mock_zcars</span>) are from older dev runs or tools — not real Airalo SIMs.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AdminEsimOrderLogsPanel
        logs={esimOrderLogs}
        loading={esimLoading}
        onRefresh={onRefresh}
        filter={esimLogsFilter}
        onFilterChange={onLogsFilterChange}
      />

      <div className="zc-card overflow-hidden mt-4" data-testid="admin-esim-activations">
        <div className="px-4 py-3 border-b border-[var(--zc-border)] eyebrow">Issued activations</div>
        {esimLoading && esimActivations.length === 0 && <div className="px-6 py-6 text-center text-[var(--zc-text-3)] text-xs">Loading…</div>}
        {!esimLoading && esimActivations.length === 0 && <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">No eSIM activations yet.</div>}
        {esimActivations.map((a) => {
          const isLive = a.provider === "airalo";
          const audit = a.provisioning_audit || {};
          return (
            <div key={a.activation_id} className="px-4 py-4 border-b border-[var(--zc-border)] last:border-0 text-sm" data-testid={`admin-esim-act-${a.activation_id}`}>
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="font-mono zc-meta">{a.activation_id}</div>
                <span className="zc-badge" style={{
                  background: isLive ? "rgba(16,185,129,0.14)" : "rgba(245,158,11,0.14)",
                  color: isLive ? "var(--zc-success)" : "var(--zc-warn,#f59e0b)",
                }}>
                  {isLive ? "Airalo live" : "Mock / dev"}
                </span>
              </div>
              <div className="mt-2 text-xs text-[var(--zc-text-2)] space-y-0.5">
                <div><b>Booking:</b> <span className="font-mono">{a.booking_id}</span> · <b>User:</b> {a.user_id}</div>
                <div><b>ICCID:</b> <span className="font-mono">{a.iccid || "—"}</span> · <b>Status:</b> {a.status}</div>
                {isLive && (
                  <div>
                    <b>Airalo order:</b> {a.airalo_order_id || "—"}
                    {a.airalo_order_code && <> · code {a.airalo_order_code}</>}
                    {audit.environment && <> · env {audit.environment}</>}
                  </div>
                )}
                {!isLive && (
                  <div style={{ color: "var(--zc-warn,#f59e0b)" }}>
                    Not a real Airalo SIM — QR uses mock ICCID/LPA payload for local UX only.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
